/**
 * <lo-product-slider>
 * Arrow controls + state for the LO Product list scroll-snap track.
 * Scrolling itself is native CSS, so the section still works if this file fails to load.
 * Scoped per instance: no globals, no shared flags, safe with multiple sections per page
 * and theme editor re-renders (connected/disconnected lifecycle).
 */
if (!customElements.get('lo-product-slider')) {
  customElements.define(
    'lo-product-slider',
    class LoProductSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        if (!this.track) return;

        this.prevButton = this.querySelector('[data-prev]');
        this.nextButton = this.querySelector('[data-next]');

        this.update = this.update.bind(this);
        this.onPrev = () => this.go(-1);
        this.onNext = () => this.go(1);
        this.onBlockSelect = this.onBlockSelect.bind(this);

        this.prevButton?.addEventListener('click', this.onPrev);
        this.nextButton?.addEventListener('click', this.onNext);
        this.track.addEventListener('scroll', this.update, { passive: true });
        document.addEventListener('shopify:block:select', this.onBlockSelect);

        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.track);

        this.update();
      }

      disconnectedCallback() {
        this.prevButton?.removeEventListener('click', this.onPrev);
        this.nextButton?.removeEventListener('click', this.onNext);
        this.track?.removeEventListener('scroll', this.update);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.resizeObserver?.disconnect();
      }

      get behavior() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      }

      /* Scroll by one "page" of fully visible items */
      go(direction) {
        const item = this.track.firstElementChild;
        if (!item) return;

        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const step = item.getBoundingClientRect().width + gap;
        const perPage = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));

        this.track.scrollBy({ left: direction * perPage * step, behavior: this.behavior });
      }

      update() {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const maxScroll = scrollWidth - clientWidth;
        const scrollable = maxScroll > 2;

        this.toggleAttribute('data-scrollable', scrollable);
        if (this.prevButton) this.prevButton.disabled = !scrollable || scrollLeft <= 2;
        if (this.nextButton) this.nextButton.disabled = !scrollable || scrollLeft >= maxScroll - 2;
      }

      /* Theme editor: bring the selected product block into view */
      onBlockSelect(event) {
        if (!this.track.contains(event.target)) return;
        const left = event.target.offsetLeft - this.track.offsetLeft;
        this.track.scrollTo({ left, behavior: this.behavior });
      }
    }
  );
}